import { type SortDirection } from '@/shared/ui';

/**
 * Creates a comparator function for sorting arrays by a top-level property.
 * Handles null/undefined by pushing them to the end.
 *
 * For nested properties, use createComparatorWithAccessor instead.
 */
export function createComparator<T>(
  sortKey: keyof T,
  sortDirection: SortDirection,
): (a: T, b: T) => number {
  return createComparatorWithAccessor((item: T) => item[sortKey], sortDirection);
}

/**
 * Creates a comparator function for sorting arrays using a custom accessor.
 * Handles null/undefined by pushing them to the end.
 *
 * Use this for nested properties:
 * ```ts
 * items.sort(createComparatorWithAccessor(
 *   (item) => item.nested?.data.name,
 *   'asc',
 * ));
 * ```
 */
function createComparatorWithAccessor<T>(
  accessor: (item: T) => unknown,
  sortDirection: SortDirection,
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const aValue = accessor(a);
    const bValue = accessor(b);

    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    const comparison = aValue < bValue ? -1 : 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  };
}
